import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Lock, ArrowLeft, Check, Sun, Award } from 'lucide-react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { BADGE_CATALOG } from '../utils/badges';
import { format } from 'date-fns';

/**
 * Trophy Case page.
 * Two sections:
 *  1. Milestones — sun-themed milestone badges (from BADGE_CATALOG)
 *  2. Achievements — workout-count achievements (5/10/30/50/100) with progress meters
 *     Sourced from /api/leaderboard/achievements so the progress bar reflects real data.
 */

function BadgeCard({ badge, isEarned, when, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * idx }}
      className={`bg-card border rounded-2xl p-6 flex flex-col items-center text-center gap-3 transition-all
        ${isEarned ? 'border-primary/60' : 'border-border opacity-80'}`}
      data-testid={`badge-card-${badge.code}`}
    >
      <div className="relative">
        <img
          src={badge.image}
          alt={badge.name}
          className={`w-24 h-24 object-contain transition-all
            ${isEarned ? 'badge-shimmer' : 'grayscale opacity-30'}`}
        />
        {!isEarned && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Lock className="h-8 w-8 text-muted-foreground/80" />
          </div>
        )}
      </div>
      <div>
        <div className="font-heading text-xl text-white">{badge.name}</div>
        <div className="text-sm text-muted-foreground mt-1">{badge.description}</div>
      </div>
      {isEarned ? (
        <div className="flex items-center gap-1 text-xs text-primary">
          <Check className="h-3.5 w-3.5" />
          <span>
            Unlocked{when ? ` · ${format(new Date(when), 'MMM d, yyyy')}` : ''}
          </span>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground uppercase tracking-wider">Locked</div>
      )}
    </motion.div>
  );
}

function AchievementCard({ ach, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.04 * idx }}
      className={`relative bg-card border rounded-2xl p-5 transition-all ${
        ach.unlocked ? 'border-primary/60' : 'border-border'
      }`}
      data-testid={`achievement-card-${ach.id}`}
    >
      {ach.unlocked && (
        <span className="absolute top-3 right-3 text-[10px] font-heading tracking-wider bg-primary text-black px-2 py-0.5 rounded-full">
          UNLOCKED
        </span>
      )}
      <div className="flex items-start gap-4">
        <div
          className={`h-14 w-14 rounded-xl flex items-center justify-center text-3xl ${
            ach.unlocked ? 'bg-primary/20' : 'bg-white/5'
          }`}
        >
          <span className={ach.unlocked ? '' : 'grayscale opacity-50'}>{ach.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className={`font-heading text-lg uppercase tracking-wider ${
              ach.unlocked ? 'text-primary' : 'text-white/70'
            }`}
          >
            {ach.name}
          </h4>
          <p className="text-muted-foreground text-sm">{ach.description}</p>
        </div>
      </div>

      {/* Progress Meter */}
      <div className="mt-4">
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-muted-foreground">Progress</span>
          <span className={ach.unlocked ? 'text-primary font-medium' : 'text-white/60'}>
            {ach.progress}/{ach.requirement}
          </span>
        </div>
        <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${ach.progress_percent}%` }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className={`h-full ${ach.unlocked ? 'bg-primary' : 'bg-white/40'}`}
          />
        </div>
      </div>

      {ach.unlocked && ach.unlocked_at && (
        <div className="mt-3 text-xs text-muted-foreground">
          Unlocked {format(new Date(ach.unlocked_at), 'MMM d, yyyy')}
        </div>
      )}
    </motion.div>
  );
}

export default function BadgesPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const earned = new Set(user?.badges || []);
  const earnedAt = user?.badges_earned_at || {};
  const milestoneBadges = Object.values(BADGE_CATALOG).filter(
    (b) => (b.category || 'milestone') === 'milestone'
  );
  const milestoneEarned = milestoneBadges.filter((b) => earned.has(b.code)).length;

  const [achievements, setAchievements] = useState([]);
  const [achLoaded, setAchLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api().get('/achievements');
        if (!active) return;
        setAchievements(res.data.achievements || []);

        // 🎉 Celebrate freshly unlocked achievements. The backend includes
        // only the ones awarded during THIS request, so we don't replay
        // confetti on every visit.
        const fresh = res.data.newly_unlocked || [];
        if (fresh.length > 0) {
          // Fire a multi-burst confetti animation from both sides of the
          // viewport — feels celebratory without one giant explosion.
          const fireBurst = (originX) => {
            confetti({
              particleCount: 80,
              spread: 70,
              startVelocity: 45,
              origin: { x: originX, y: 0.7 },
              colors: ['#FFD700', '#FFA500', '#FFFFFF', '#38BDF8', '#A78BFA'],
            });
          };
          fireBurst(0.2);
          setTimeout(() => fireBurst(0.8), 200);
          setTimeout(() => fireBurst(0.5), 400);

          // One toast per new achievement, staggered so users can read each.
          fresh.forEach((ach, i) => {
            setTimeout(() => {
              toast.success(`Achievement Unlocked: ${ach.name}!`, {
                description: ach.description,
                duration: 6000,
              });
            }, i * 800);
          });
        }
      } catch {
        if (active) setAchievements([]);
      }
      if (active) setAchLoaded(true);
    })();
    return () => {
      active = false;
    };
  }, [api]);

  const achUnlocked = achievements.filter((a) => a.unlocked).length;
  const totalUnlocked = milestoneEarned + achUnlocked;
  const totalAvailable = milestoneBadges.length + achievements.length;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-white"
          data-testid="badges-back-btn"
          aria-label="back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Trophy className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl uppercase tracking-wider">Trophy Case</h1>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-2xl p-6 mb-8"
        >
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-heading text-4xl text-primary">
                {totalUnlocked}{' '}
                <span className="text-white">
                  / {achLoaded ? totalAvailable : milestoneBadges.length}
                </span>
              </div>
              <div className="text-muted-foreground text-sm mt-1">
                trophies unlocked — keep pushing to collect them all
              </div>
            </div>
            <Trophy className="h-10 w-10 text-primary opacity-40" />
          </div>
        </motion.div>

        {/* Achievements (workout-count, progress-meter style) */}
        <section className="mb-10" data-testid="badges-section-achievement">
          <div className="flex items-center gap-3 mb-4">
            <Award className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl uppercase tracking-wider">Achievements</h2>
            <span className="text-xs text-muted-foreground">
              {achUnlocked} / {achievements.length || 5}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Total lifetime workouts completed</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {achievements.map((a, idx) => (
              <AchievementCard key={a.id} ach={a} idx={idx} />
            ))}
          </div>
        </section>

        {/* Milestones (sun-themed badges) */}
        <section className="mb-10" data-testid="badges-section-milestone">
          <div className="flex items-center gap-3 mb-4">
            <Sun className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl uppercase tracking-wider">Milestones</h2>
            <span className="text-xs text-muted-foreground">
              {milestoneEarned} / {milestoneBadges.length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Premium badges for major lifetime workout milestones
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {milestoneBadges.map((b, idx) => (
              <BadgeCard
                key={b.code}
                badge={b}
                isEarned={earned.has(b.code)}
                when={earnedAt[b.code]}
                idx={idx}
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
